const Environment = require('./environment');

class Interpreter {
    constructor(outputCallback, inputCallback, options = {}) {
        this.outputCallback = outputCallback || console.log;
        this.inputCallback = inputCallback || (async () => "default_input");
        this.globalEnv = new Environment();
        this.loopLimit = options.loopLimit || Infinity;
    }

    async execute(ast) {
        if (ast.type === 'Program') {
            return await this.executeBlock(ast.body, this.globalEnv);
        }
        throw new Error("AST must start with a Program node.");
    }

    async executeBlock(statements, environment) {
        for (const statement of statements) {
            await this.executeStatement(statement, environment);
        }
    }

    async executeStatement(stmt, env) {
        switch (stmt.type) {
            case 'PrintStatement':
                const value = await this.evaluate(stmt.expression, env);
                this.outputCallback(value);
                break;
            case 'VariableDeclaration':
                const initVal = await this.evaluate(stmt.value, env);
                env.define(stmt.name, initVal);
                break;
            case 'Assignment':
                const assignVal = await this.evaluate(stmt.value, env);
                if (stmt.operator === '+=') {
                    const curr = env.get(stmt.name);
                    env.assign(stmt.name, curr + assignVal);
                } else {
                    env.assign(stmt.name, assignVal);
                }
                break;
            case 'MemberAssignment':
                const targetArray = env.get(stmt.object);
                if (!Array.isArray(targetArray)) {
                    throw new Error(`Cannot assign to index of non-array variable '${stmt.object}'`);
                }
                const indexVal = await this.evaluate(stmt.index, env);
                const assignValMember = await this.evaluate(stmt.value, env);
                if (stmt.operator === '+=') {
                    targetArray[indexVal] += assignValMember;
                } else {
                    targetArray[indexVal] = assignValMember;
                }
                break;
            case 'IfStatement':
                if (this.isTruthy(await this.evaluate(stmt.condition, env))) {
                    await this.executeBlock(stmt.thenBranch, new Environment(env));
                } else if (stmt.elseBranch) {
                    await this.executeBlock(stmt.elseBranch, new Environment(env));
                }
                break;
            case 'BreakStatement':
                throw { type: 'BREAK' };
            case 'ContinueStatement':
                throw { type: 'CONTINUE' };
            case 'UntilStatement':
                // Treating 'until' as 'while' based on language analysis
                try {
                    let iterations = 0;
                    while (this.isTruthy(await this.evaluate(stmt.condition, env))) {
                        iterations++;
                        if (iterations > this.loopLimit) {
                            throw new Error(`Loop limit of ${this.loopLimit} exceeded. Potential infinite loop detected.`);
                        }
                        try {
                            await this.executeBlock(stmt.body, new Environment(env));
                        } catch (e) {
                            if (e.type === 'BREAK') break;
                            if (e.type === 'CONTINUE') continue;
                            throw e;
                        }
                    }
                } catch (e) {
                    if (e.type === 'BREAK') { } // Break out of loop entirely
                    else throw e;
                }
                break;
            case 'ForStatement':
                const loopEnv = new Environment(env);
                if (stmt.init) await this.executeStatement(stmt.init, loopEnv);
                try {
                    let iterations = 0;
                    while (this.isTruthy(await this.evaluate(stmt.condition, loopEnv))) {
                        iterations++;
                        if (iterations > this.loopLimit) {
                            throw new Error(`Loop limit of ${this.loopLimit} exceeded. Potential infinite loop detected.`);
                        }
                        try {
                            await this.executeBlock(stmt.body, new Environment(loopEnv));
                        } catch (e) {
                            if (e.type === 'BREAK') break;
                            if (e.type === 'CONTINUE') {
                                // execute update and then continue loop
                                if (stmt.update) {
                                    if (stmt.update.type === 'Assignment') {
                                        await this.executeStatement(stmt.update, loopEnv);
                                    } else {
                                        await this.evaluate(stmt.update, loopEnv);
                                    }
                                }
                                continue;
                            }
                            throw e;
                        }
                        if (stmt.update) {
                            if (stmt.update.type === 'Assignment') {
                                await this.executeStatement(stmt.update, loopEnv);
                            } else {
                                await this.evaluate(stmt.update, loopEnv);
                            }
                        }
                    }
                } catch (e) {
                    if (e.type === 'BREAK') { }
                    else throw e;
                }
                break;
            default:
                throw new Error(`Unknown statement type: ${stmt.type}`);
        }
    }

    async evaluate(expr, env) {
        switch (expr.type) {
            case 'Literal':
                return expr.value;
            case 'ArrayLiteral':
                const arr = [];
                for (const e of expr.elements) {
                    arr.push(await this.evaluate(e, env));
                }
                return arr;
            case 'Identifier':
                return env.get(expr.name);
            case 'InputExpression':
                let promptText = "";
                if (expr.prompt) {
                    promptText = await this.evaluate(expr.prompt, env);
                }
                return await this.inputCallback(promptText);
            case 'MemberExpression':
                const obj = await this.evaluate(expr.object, env);
                const idx = await this.evaluate(expr.index, env);
                if (!Array.isArray(obj) && typeof obj !== 'string') {
                    throw new Error("Only arrays and strings can be indexed.");
                }
                return obj[idx];
            case 'PropertyExpression':
                const target = await this.evaluate(expr.object, env);
                if (expr.property === 'LENGTH') {
                    if (Array.isArray(target) || typeof target === 'string') {
                        return target.length;
                    }
                    throw new Error("Property 'length' is only supported for arrays and strings.");
                }
                throw new Error(`Unknown property: ${expr.property}`);
            case 'LogicalExpression':
                const leftL = await this.evaluate(expr.left, env);
                if (expr.operator === 'OR') {
                    return this.isTruthy(leftL) ? leftL : await this.evaluate(expr.right, env);
                } else if (expr.operator === 'AND') {
                    return !this.isTruthy(leftL) ? leftL : await this.evaluate(expr.right, env);
                }
                throw new Error(`Unknown logical operator: ${expr.operator}`);
            case 'BinaryExpression':
                const left = await this.evaluate(expr.left, env);
                const right = await this.evaluate(expr.right, env);
                return this.applyOperator(expr.operator, left, right);
            case 'UnaryExpression':
                const rightVal = await this.evaluate(expr.right, env);
                if (expr.operator === '-') {
                    return -rightVal;
                }
                if (expr.operator === '!') {
                    return !this.isTruthy(rightVal);
                }
                throw new Error(`Unknown unary operator: ${expr.operator}`);
            default:
                throw new Error(`Unknown expression type: ${expr.type}`);
        }
    }

    applyOperator(op, left, right) {
        switch (op) {
            case '+': return left + right;
            case '-': return left - right;
            case '*': return left * right;
            case '/': return left / right;
            case '%': return left % right;
            case '>': return left > right;
            case '>=': return left >= right;
            case '<': return left < right;
            case '<=': return left <= right;
            case '==': return left === right;
            case '!=': return left !== right;
            default: throw new Error(`Unknown operator: ${op}`);
        }
    }

    isTruthy(value) {
        if (value === null || value === undefined) return false;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value !== 0;
        return true;
    }
}

module.exports = Interpreter;
